/**
 * i18n bootstrap — Sister Signal.
 *
 * Phase 1: English + Neutral Latin American Spanish (es).
 *
 * Tone in every locale must stay: calm, intelligent, emotionally safe,
 * private, feminine, grounded, culturally warm. Never corporate, legalistic,
 * robotic, cold, or fear-marketing.
 *
 * - Manual selection only (no auto-detect for v1 per product call).
 * - Persisted in localStorage under key `ss_lang`, and also synced to the
 *   user profile via PATCH /api/me/preferences.
 * - Falls back to English for any missing key.
 *
 * To add a new language later:
 *   1. drop a JSON file into ./locales/<code>.json
 *   2. import it below and add to `resources`
 *   3. add the option to <LanguagePicker /> in Onboarding + Settings
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import en from "./locales/en.json";
import es from "./locales/es.json";

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", native: "English" },
  { code: "es", label: "Español", native: "Español" },
];

export const DEFAULT_LANGUAGE = "en";
const STORAGE_KEY = "ss_lang";

function readStoredLanguage() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v && SUPPORTED_LANGUAGES.some((l) => l.code === v)) return v;
  } catch { /* ignore */ }
  return DEFAULT_LANGUAGE;
}

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
    },
    lng: readStoredLanguage(),
    fallbackLng: DEFAULT_LANGUAGE,
    interpolation: { escapeValue: false }, // React already escapes
    returnEmptyString: false,
  });

/** Persist + switch the active language. Safe to call repeatedly. */
export function setLanguage(code) {
  if (!SUPPORTED_LANGUAGES.some((l) => l.code === code)) return;
  try { window.localStorage.setItem(STORAGE_KEY, code); } catch { /* ignore */ }
  if (i18n.language !== code) i18n.changeLanguage(code);
}

export function getLanguage() {
  return i18n.language || DEFAULT_LANGUAGE;
}

export default i18n;
